<template>
  <nav class="mobile-menu text-white md:hidden font-overpass" :class="{ 'menu-open': mobileMenuOpen || mobileAppMenuOpen }">
    <header>
      <TButton
        icon
        class="logo"
        @click="toggleAppMenu">
        <InlineSVG :src="require(`./assets/logo.svg`)"/>
      </TButton>

      <div class="center">
        <transition name="slide-header">
          <div v-if="mobileMenuOpen">
              Menu
          </div>
          <div v-else-if="mobileAppMenuOpen">
              All Apps
          </div>
          <h1 v-else>
              {{ appName }}
          </h1>
        </transition>
      </div>

      <div class="mobile-toggle">
        <TButton
          v-if="menuItems.length"
          icon
          class="mobile-toggle-button"
          :class="{ open: mobileMenuOpen }"
          @click="toggleMenu">
          <span/>
        </TButton>
      </div>
    </header>

    <MenuItems
      class="products-menu"
      :pose="mobileAppMenuOpen ? 'visible' : 'hidden'">
      <div class="products">
        <div
          v-for="{ name, route, url, icon, key } in apps"
          :class="[`product`, `${key}`]">
          <a
            v-if="route"
            :href="resolveRoute(route)"
          >
            <InlineSVG :src="icon"/>
          </a>
          <a
            v-else
            :href="url"
          >
            <InlineSVG :src="icon"/>
          </a>
          <div class="label">{{ name }}</div>
        </div>
      </div>
    </MenuItems>

    <MenuItems
      class="main"
      :pose="mobileMenuOpen ? 'visible' : 'hidden'">
      <ul @click="toggleMenu">
        <li v-for="item in menuItems">
          <inertia-link :href="item.route">
            {{ item.label }}
          </inertia-link>

          <ul v-if="item.subs">
            <li v-for="subItem in item.subs">
              <inertia-link :href="subItem.route">
                {{ subItem.label }}
              </inertia-link>
            </li>
          </ul>
        </li>
      </ul>

      <div class="flex justify-center mb-6" @click="toggleMenu">
        <AuthButtons v-if="! $page.props.user"/>
        <template v-else>
          <ul>
            <li>
              <inertia-link :href="$route('logout')">
                Logout
              </inertia-link>
            </li>
          </ul>
        </template>
      </div>
      <!-- <PortalTarget name="mobile-footer"/> -->
    </MenuItems>
  </nav>
</template>

<script>
import Vue from 'vue'
// import { mapActions, mapGetters, mapState } from 'vuex'
import posed from 'vue-pose'
import apps from './apps'
import menuItems from './menu-items'
import InlineSVG from 'vue-inline-svg'
import TButton from './TButton.vue'
import AuthButtons from '@/components/shared/AuthButtons.vue'

export default {
  name: `MobileMenu`,

  props: {
    user: Object,
  },

  data() {
    return {
      mobileMenuOpen: false,
      mobileAppMenuOpen: false,
      appName: 'Media',
    }
  },

  components: {
    InlineSVG,
    TButton,
    AuthButtons,
    MenuItems: posed.div({
      visible: {
        opacity: 1,
        height: `auto`
      },
      hidden: {
        opacity: 0,
        height: 0,
        overflow: `hidden`
      }
    })
  },

  computed: {
    menuItems() {
      return menuItems
    },

    apps() {
      if (this.isAdmin) {
        return apps
      }

      if (this.appAccess === null) {
        return apps
      }

      return apps.filter(app => this.appAccess.includes(app.key))
    },

    isAdmin() {
      return this.$get(this.user, 'claims.admin', false)
    },

    appAccess() {
      return this.$get(this.user, 'claims.access', null)
    },

    ssoCallbackUrl() {
      return this.$get(this.user, 'claims.callback', null)
    },
  },

  watch: {
    mobileMenuOpen(newVal) {
      this.toggleBodyScroll(newVal)
    },
    mobileAppMenuOpen(newVal) {
      this.toggleBodyScroll(newVal)
    }
  },

  methods: {
    toggleBodyScroll(disable) {
      if (disable) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },

    resolveRoute(route) {
      if (! this.ssoCallbackUrl) {
        return route
      }

      route = route.startsWith('/') ? route.substr(1) : route

      return `${this.ssoCallbackUrl}${route}`
    },

    /**
     * Toggles the main menu
     */
    toggleMenu() {
      this.mobileAppMenuOpen = false
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    /**
     * Toggles the app menu
     */
    toggleAppMenu() {
      if (! this.$page.props.hasAppMenu) {
        return
      }

      this.mobileMenuOpen = false
      this.mobileAppMenuOpen = !this.mobileAppMenuOpen
    }
  },

  beforeDestroy() {
    // Clean up body scroll lock when component is destroyed
    document.body.style.overflow = ''
  }
}
</script>

<style lang="scss" scoped>
* {
  --yank-fire: #212A4A;
  --aquamarine: #5BD9A4;
  --yank-mild: #67749E;
  --yank: #181F36;
  --color: var(--yank);
  --platinum-hot: #f7f7f7;
  --platinum: #e8e8e8;
  --payne: #4f5e72;
  --payne-fire: #6c7e99;
  --radius: '4px';
  --element-shadow-focused: '1px 12px 37px rgba(64,64,64,0.1),0px 4px 7px rgba(55,54,77,0.19)';
  --medium: 500;
  --semi-bold: 600;
  --font-size-xs: .75rem;
  --font-size-sm: .875rem;
  --layout-background: #202a47 url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzczIiBoZWlnaHQ9IjMyMCIgdmlld0JveD0iMCAwIDM3MyAzMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgICA8cGF0aCBvcGFjaXR5PSIwLjE1IiBkPSJNMCAwVjMxOS44MjhIMzczTDM3MyAyMTguODI4QzM2MC4xNTggMTk0LjA3NSAzNDMuMTM0IDE2OS45MSAzMjAuODA3IDE0Ny40MjlDMjQzLjU2MyA2OS42NDc5IDEwNy4zNjQgMjQuNzIzMSAwIDBaIiBmaWxsPSJ1cmwoI3BhaW50MF9saW5lYXIpIi8+CiAgICA8ZGVmcz4KICAgICAgICA8bGluZWFyR3JhZGllbnQgaWQ9InBhaW50MF9saW5lYXIiIHgxPSItMjQ4LjQ2NiIgeTE9IjEyNC4xNjgiIHgyPSIxOTEuNjE5IiB5Mj0iNDczLjI2NiIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPgogICAgICAgICAgICA8c3RvcCBzdG9wLWNvbG9yPSJ3aGl0ZSIvPgogICAgICAgICAgICA8c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IndoaXRlIiBzdG9wLW9wYWNpdHk9IjAiLz4KICAgICAgICA8L2xpbmVhckdyYWRpZW50PgogICAgPC9kZWZzPgo8L3N2Zz4K) no-repeat;
  --font-family-headings: "Overpass",sans-serif;
}

.mobile-menu {
    flex: 0 0 auto;
    background: var(--layout-background);
    background-position: 0% 145%;
    
    &.menu-open {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 9999;
        overflow-y: auto;
    }
}

header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 68px;
    padding: 0 24px;
}

.logo {
    flex: 0 0 36px;
    color: var(--aquamarine);

    svg {
        width: 30px;
    }
}

.center {
    flex: 1;
    position: relative;

    > * {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    div {
        font-weight: var(--semi-bold);
        font-size: .875rem;
        line-height: 1.3;
        text-transform: uppercase;
        color: #a3b1c7;
        background: #181f36;
        padding: 8px 16px;
        border-radius: 16px;
        letter-spacing: 2px;
        white-space: nowrap;
    }
}

.slide-header {
    &-enter-active,
    &-leave-active {
        transition: all 240ms;
    }

    &-enter,
    &-leave-to {
        opacity: 0;
    }
}

h1 {
    margin: 0;
    font-size: 1.375rem;
    width: 100%;
    max-width: calc(100% - 32px);
    line-height: 1.25;
    text-align: center;
}

.mobile-toggle {
    flex: 0 0 36px;
}

.mobile-toggle-button {
    position: relative;
    width: 36px;
    height: 36px;

    span {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 17px;
        height: 2px;
        background-color: white;
        border-radius: 1px;
        transition: all 180ms linear;

        &::before,
        &::after {
            content: '';
            position: absolute;
            left: 0;
            height: 2px;
            background-color: white;
            border-radius: inherit;
            transition: inherit;
        }

        &::before {
            top: -6px;
            width: 17px;
        }

        &::after {
            top: 6px;
            width: 12px;
        }
    }

    &.open {
        span {
            background-color: transparent;

            &::before,
            &::after {
                top: 0;
                left: -3px;
                width: 24px;
            }

            &::before {
                transform: rotate(45deg);
            }

            &::after {
                transform: rotate(-45deg);
            }
        }
    }
}

.products-menu {
    display: flex;
    justify-content: center;
}

.products {
    display: grid;
    grid-template-columns: repeat(3, minmax(84px, 1fr));
    grid-gap: 24px;
    justify-items: center;
    margin: 16px 0 32px;
}

.product {
    display: flex;
    flex-direction: column;
    align-items: center;

    a {
        display: flex;
        height: 60px;
        width: 60px;
        color: white;
        border-radius: 16px;
        margin-bottom: 16px;
        align-items: center;
        justify-content: center;

        svg {
            width: 100%;
            height: auto;
            max-width: 32px;
            max-height: 32px;
        }
    }

    .label {
        font-weight: var(--medium);
        font-size: .625rem;
        line-height: calc(4 / 3);
        text-transform: uppercase;
        letter-spacing: 0.2em;
    }

    &.people {
        a {
            background-color: hsl(101, 57%, 58%);
        }
    }

    &.giving {
        a {
            background-color: hsl(155, 62%, 60%);
        }
    }

    &.messaging {
        a {
            background-color: hsl(36, 98%, 55%);
        }
    }

    &.events {
        a {
            background-color: hsl(263, 98%, 55%);
        }
    }

    &.apps {
        a {
            background-color: hsl(211, 98%, 55%);
        }
    }

    &.sites {
        a {
            background-color: hsl(183, 78%, 49%);
        }
    }

    &.chms {
        a {
            background-color: hsl(7, 89%, 64%);
        }
    }

    &.media {
        a {
            background-color: hsl(52, 84%, 59%);
        }
    }

    &.pay {
        a {
            background-color: hsl(317, 65%, 59%);
        }
    }

    &.legacy {
        a {
            background-color: hsl(128, 57%, 62%);
        }
    }
}

.main {
    ul {
        list-style: none;
        margin: 16px 0 24px;
        padding: 0;

        li {
            padding: 16px 0;
            text-align: center;
            font: var(--semi-bold) 1.375rem/1.2 var(--font-family-headings);

            a {
                text-decoration: none;
            }
        }

        ul {
            margin: 0;

            li {
                a {
                    font-size: 1.125rem;
                    opacity: 0.75;
                }
            }
        }
    }
}
</style>
